import { Box, Button, Icon, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  DATE_FORMAT_DAY,
  DATE_FORMAT_MONTH_YEAR,
  FilterKeys,
  useGetAssessmentQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { RecurrenceInfo } from '@schooly/components/recurring';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import {
  CrossIcon,
  EditIcon,
  IncompleteSvg,
  Loading,
  LockIcon,
  ReportGeneratedIcon,
  ReportsIcon,
  SimpleButton,
  SquareCard,
  TruncatedLinks,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { AccessDenied } from '@schooly/style';
import { CollapsiblePanel } from '../../../components/common/CollapsiblePanel';
import { ModalNavigation } from '../../../components/common/ModalNavigation';
import { ModalLarge, ModalMain } from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeaderV2 } from '../../../components/uikit-components/Modal/ModalHeaderV2';
import AssessmentStatuses from '../../../constants/assessmentStatuses';
import { useCollapsiblePanel } from '../../../context/collapsiblePanel/useCollapsiblePanel';
import { useRouter } from '../../../context/router/useRouter';
import { allowRenderAssessments } from '../../../helpers/renderRoutesByPermissions';
import useAppLocation from '../../../hooks/useAppLocation';
import useQueryStringParams from '../../../hooks/useQueryStringParams';
import { DropdownMarkbookSettings } from '../Markbook/MarkbookSettings/DropdownMarkbookSettings';
import {
  DEFAULT_MARKBOOK_VIEW_SETTINGS,
  MarkbookOverrideValueAndDisable,
  MarkbookViewSettings,
} from '../Markbook/MarkbookSettings/MarkbookSettings';
import { shouldDisableScoreInPercentage } from '../Markbook/MarkbookSettings/utils';
import { AssessmentsPreviewActions } from './AssessmentsPreviewActions';
import { AssessmentsPreviewExport } from './AssessmentsPreviewExport/AssessmentsPreviewExport';
import { AssessmentsPreviewModalContent } from './AssessmentsPreviewModalContent';
import { AssessmentsPreviewModalSideBar } from './AssessmentsPreviewModalSideBar';
// TODO: hide body scroll
// TODO: adjust entries spin (brings content vertical scroll)
// TODO: remake router stack mechanim
const AssessmentsPreviewModal: FC = () => {
  const { $t } = useIntl();
  const navigate = useNavigate();
  const location = useAppLocation();
  const { id } = useParams<'id'>();
  const { closeAndClean } = useRouter();
  const { permissions } = useAuth();
  const invalidateQueries = useInvalidateListQueriesFor('assessment');
  const { group_id } = useQueryStringParams<'group_id'>();
  const { state } = useCollapsiblePanel();
  const isPanelOpen = state.isOpen;
  const { getAssessmentEvaluationTitle } = useAssessmentLabels();

  const [_settings, setSettings] = useState<MarkbookViewSettings>(DEFAULT_MARKBOOK_VIEW_SETTINGS);

  const {
    data,
    isFetching: fetching,
    refetch,
  } = useGetAssessmentQuery(
    { id: id ?? '' },
    {
      enabled: Boolean(id),
      refetchOnMount: 'always',
    },
  );

  const overrideValueAndDisable: MarkbookOverrideValueAndDisable = useMemo(() => {
    return {
      scoreInPercentage: shouldDisableScoreInPercentage(data ? [data] : []) ? false : undefined,
    };
  }, [data]);

  const settings = useMemo(() => {
    return {
      ..._settings,
      scoreInPercentage: overrideValueAndDisable.scoreInPercentage ?? _settings.scoreInPercentage,
    };
  }, [_settings, overrideValueAndDisable]);

  const isIncomplete =
    (data?.status !== AssessmentStatuses.Published && !data?.methods.length) ||
    !data?.groups.length;

  const autoGeneratedFromReport = data?.autogenerated;

  const assessmentDate = newDateTimezoneOffset(data?.assessment_date);
  const assessmentDay = format(assessmentDate, DATE_FORMAT_DAY);
  const assessmentMonthYear = format(assessmentDate, DATE_FORMAT_MONTH_YEAR);

  const userHasAccess = allowRenderAssessments(permissions);

  const showEditButton = useMemo(() => {
    return Boolean(
      permissions.includes('assessment_viewer') &&
        data?.can_be_edited &&
        data.status !== AssessmentStatuses.Published,
    );
  }, [data?.status, data?.can_be_edited, permissions]);

  const handleEditButtonClick = useCallback(() => {
    navigate(`/assessments/${id}/edit`, { state: location.state });
  }, [id, location.state, navigate]);

  const handleActionUpdate = useCallback(async () => {
    await refetch();
    invalidateQueries();
  }, [invalidateQueries, refetch]);

  const types = useMemo(
    () =>
      data?.methods
        .map((method) => $t({ id: `assessments-Type-${method.method_type}` }))
        .join('  + '),

    [data?.methods, $t],
  );

  const currentGroup = group_id && data ? data.groups.find((g) => g.id === group_id) : undefined;

  const getTitle = () => {
    if (!data) return '';

    const assessmentName = data.name;
    const evaluationTitle = getAssessmentEvaluationTitle(data);

    return `${assessmentName}${evaluationTitle ? ` • ${evaluationTitle}` : ''} `;
  };

  const renderContent = () => {
    if (fetching) {
      return <Loading />;
    }

    if (!userHasAccess) {
      return <AccessDenied />;
    }

    return (
      <>
        <ModalHeaderV2
          title={
            <Stack direction="row" gap={2.5} alignItems="center">
              <SquareCard sx={{ '.MuiTypography-root': { color: 'common.grey2' } }}>
                <Typography mt={0.25} variant="h2">
                  {assessmentDay}
                </Typography>
                <Typography variant="caption">{assessmentMonthYear}</Typography>
              </SquareCard>

              <Stack>
                <Typography
                  variant="inherit"
                  sx={{ '& .MuiIconButton-root': { color: 'primary.main' } }}
                >
                  {getTitle()}
                  {data?.recurring_state && (
                    <RecurrenceInfo
                      recurringState={data.recurring_state}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          { pathname: '/assessments' },
                          {
                            state: {
                              [FilterKeys.RecurrenceId]: data.recurring_state?.recurrence_id,
                            },
                          },
                        );
                      }}
                    />
                  )}
                </Typography>
                <Stack flexDirection="row" gap={5}>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }} color="text.primary">
                    {types}
                  </Typography>

                  {data?.reports?.length ? (
                    <Stack direction="row" gap={1}>
                      <Icon fontSize="small" sx={{ color: 'common.grey2' }}>
                        {data.autogenerated ? <ReportGeneratedIcon /> : <ReportsIcon />}
                      </Icon>

                      <Stack overflow="hidden" width={200}>
                        <TruncatedLinks
                          items={data.reports.map(({ name, id }) => ({
                            name: name,
                            link: { to: `/reports/${id}` },
                          }))}
                          typographyProps={{
                            color: 'common.grey2',
                            sx: { '&[href]:hover': { color: 'primary.main' } },
                          }}
                          tooltipProps={{
                            arrow: false,
                            placement: 'bottom-start',
                            componentsProps: {
                              tooltip: {
                                sx: {
                                  px: 0.5,
                                  py: 0.25,
                                  minWidth: 300,
                                  maxHeight: 400,
                                  overflowY: 'scroll',
                                },
                              },
                            },
                            slotProps: {
                              popper: {
                                sx: (theme) => ({
                                  '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip':
                                    { margin: theme.spacing(-2.5, 0, 0, -0.5) },
                                }),
                              },
                            },
                          }}
                        />
                      </Stack>
                    </Stack>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
          }
          withGoBack
          active
        >
          {data && (
            <AssessmentsPreviewActions
              assessment={data}
              isIncomplete={isIncomplete}
              onUpdate={handleActionUpdate}
            />
          )}

          {!isIncomplete && (
            <DropdownMarkbookSettings
              settings={settings}
              onSetSettings={setSettings}
              overrideValueAndDisable={overrideValueAndDisable}
            />
          )}

          {Boolean(data?.groups?.length) && <AssessmentsPreviewExport data={data} />}
          {showEditButton &&
            (autoGeneratedFromReport ? (
              <Tooltip
                title={
                  <Stack alignItems="flex-start" gap={1}>
                    <Typography>{$t({ id: 'assessments-AutoGeneratedEdit' })}</Typography>
                    <SimpleButton
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/reports/${data?.generated_from_report_id}`);
                      }}
                      sx={(theme) => ({
                        '.svg-icon': {
                          width: theme.spacing(2),
                          height: theme.spacing(2),
                        },
                        backgroundColor: 'transparent',
                        fontSize: theme.spacing(1.5),
                      })}
                      startIcon={<ReportGeneratedIcon />}
                    >
                      {$t({ id: 'assessments-GoToReport' })}
                    </SimpleButton>
                  </Stack>
                }
              >
                <Box
                  sx={(theme) => ({
                    ':hover > .MuiIconButton-root': { color: theme.palette.primary.main },
                  })}
                >
                  <IconButton inverse disabled>
                    <LockIcon />
                  </IconButton>
                </Box>
              </Tooltip>
            ) : (
              <IconButton onClick={handleEditButtonClick}>
                <EditIcon />
              </IconButton>
            ))}
          <IconButton onClick={closeAndClean}>
            <CrossIcon />
          </IconButton>
        </ModalHeaderV2>
        <ModalMain>
          {isIncomplete ? (
            <Stack alignItems="center" justifyContent="center" gap={2.5} width="100%">
              <IncompleteSvg />
              <Typography
                variant="h3"
                color="text.primary"
                sx={{ textAlign: 'center', maxWidth: 300 }}
              >
                {$t({ id: 'assessments-NotConfigured' })}
              </Typography>
              {showEditButton && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => navigate('edit', { state: { replace: true } })}
                >
                  {$t({ id: 'action-ContinueSetup' })}
                </Button>
              )}
            </Stack>
          ) : (
            <>
              <CollapsiblePanel getSxProps={() => ({ width: 266, maxWidth: 266 })}>
                <AssessmentsPreviewModalSideBar assessment={data} />
              </CollapsiblePanel>
              <AssessmentsPreviewModalContent
                groupId={group_id}
                assessment={data}
                settings={settings}
              />
            </>
          )}
        </ModalMain>
        {!!currentGroup && !isPanelOpen && !!data?.groups.length && (
          <ModalNavigation
            selectedItem={currentGroup}
            items={data.groups}
            onClick={(g) => {
              navigate({ search: `?group_id=${g.id}` }, { state: location.state });
            }}
            getItemLabel={(r) => r.name}
          />
        )}
      </>
    );
  };

  return (
    <ModalLarge open onClose={closeAndClean}>
      {renderContent()}
    </ModalLarge>
  );
};

export default AssessmentsPreviewModal;
